import {
    isFurnizorTabPriceModified,
    isFurnizorTabStructureModified,
    resolveDraftTablePricing,
    resolveLiveFurnizorTabsForDraftCompare,
    findMatchingLiveFurnizorTab,
    selectDraftFurnizorTabsForDisplay,
} from '@/utils/priceListTablePricing';
import { parseNumericInput } from '@/utils/priceListProductCalculator';

const PRICE_ABS_EPS = 0.02;
const PRICE_REL_EPS = 0.002;

/**
 * @param {unknown} value
 * @returns {number|null}
 */
function toNullableNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const num = parseNumericInput(value);
    return Number.isFinite(num) ? num : null;
}

/**
 * @param {unknown} value
 * @returns {string}
 */
function normText(value) {
    if (value === null || value === undefined) {
        return '';
    }
    return String(value).trim();
}

/**
 * @param {unknown} value
 * @returns {boolean|null}
 */
function toNullableBool(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'boolean') {
        return value;
    }
    const raw = String(value).trim().toLowerCase();
    if (['1', 'true', 'yes', 'da'].includes(raw)) {
        return true;
    }
    if (['0', 'false', 'no', 'nu'].includes(raw)) {
        return false;
    }
    return null;
}

/**
 * Compare two values when the draft actually carries the field.
 * Missing live values must not invent tip-B changes (old lists / sparse payloads).
 *
 * @param {unknown} draftVal
 * @param {unknown} liveVal
 * @param {'text'|'bool'|'json'} [kind='text']
 * @returns {boolean}
 */
function draftFieldMeaningfullyDiffers(draftVal, liveVal, kind = 'text') {
    if (draftVal === undefined) {
        return false;
    }
    if (kind === 'bool') {
        const left = toNullableBool(draftVal);
        const right = toNullableBool(liveVal);
        if (left == null && right == null) {
            return false;
        }
        if (right == null) {
            return false;
        }
        return Boolean(left) !== Boolean(right);
    }
    if (kind === 'json') {
        if (liveVal === undefined || liveVal === null) {
            return false;
        }
        try {
            return JSON.stringify(draftVal ?? null) !== JSON.stringify(liveVal ?? null);
        } catch (error) {
            return String(draftVal) !== String(liveVal);
        }
    }
    const left = normText(draftVal);
    const right = normText(liveVal);
    if (!left && !right) {
        return false;
    }
    // Live missing/empty: do not treat draft-filled defaults as tip B.
    if (!right) {
        return false;
    }
    return left !== right;
}

/**
 * True when old/new prices differ beyond rounding noise.
 * null → number counts as a change (price introduced).
 *
 * @param {unknown} oldVal
 * @param {unknown} newVal
 * @returns {boolean}
 */
export function pricesMeaningfullyDiffer(oldVal, newVal) {
    const oldNum = toNullableNumber(oldVal);
    const newNum = toNullableNumber(newVal);
    if (oldNum == null && newNum == null) {
        return false;
    }
    if (oldNum == null || newNum == null) {
        return true;
    }
    const abs = Math.abs(oldNum - newNum);
    if (abs <= PRICE_ABS_EPS) {
        return false;
    }
    const rel = abs / Math.max(Math.abs(oldNum), Math.abs(newNum), 1e-9);
    return rel > PRICE_REL_EPS;
}

/**
 * @param {Object|null|undefined} tab
 * @returns {boolean}
 */
function isExplicitNonDefaultFurnizor(tab) {
    if (!tab || typeof tab !== 'object') {
        return false;
    }
    if (tab.isDefault === true || tab.is_default === true) {
        return false;
    }
    return tab.isDefault === false || tab.is_default === false;
}

/**
 * @param {Object|null|undefined} draft
 * @returns {Object[]}
 */
function draftPayloadFurnizorTabs(draft) {
    const detail = draft?.payload?.pricing_detail || {};
    if (Array.isArray(detail.furnizorPricings)) {
        return detail.furnizorPricings;
    }
    if (Array.isArray(detail.furnizor_pricings)) {
        return detail.furnizor_pricings;
    }
    return [];
}

/**
 * Sale price stored on a furnizor tab (override wins when it is on).
 *
 * @param {Object|null|undefined} tab
 * @returns {number|null}
 */
function readTabSalePrice(tab) {
    if (!tab || typeof tab !== 'object') {
        return null;
    }
    const overrideOn = Boolean(
        tab.overrideListPriceRonIncVatFinal
        ?? tab.override_list_price_ron_inc_vat_final,
    );
    if (overrideOn) {
        const override = toNullableNumber(
            tab.overrideListPriceRonIncVatFinalValue
            ?? tab.override_list_price_ron_inc_vat_final_value,
        );
        if (override != null) {
            return override;
        }
    }
    return toNullableNumber(
        tab.listPriceRonIncVatFinal
        ?? tab.list_price_ron_inc_vat_final
        ?? tab.manualListPriceRonIncVatFinal,
    );
}

/**
 * @param {Object|null|undefined} tab
 * @returns {number|null}
 */
function readTabEntryPrice(tab) {
    return toNullableNumber(
        tab?.entryPriceRonExVat
        ?? tab?.entry_price_ron_ex_vat,
    );
}

/**
 * Non-default furnizor entry/sale (or the inputs that produce them) vs live.
 * Product-level price is the default furnizor only, so this path is required
 * for secondary suppliers to count as tip A.
 *
 * @param {Object} draft
 * @param {Object|null} liveItem
 * @returns {boolean}
 */
function nonDefaultFurnizorHasEntryOrSalePriceChange(draft, liveItem) {
    if (!liveItem) {
        return false;
    }
    const tabs = draftPayloadFurnizorTabs(draft);
    if (!tabs.length) {
        return false;
    }
    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    for (const tab of tabs) {
        if (!isExplicitNonDefaultFurnizor(tab)) {
            continue;
        }
        const liveTab = findMatchingLiveFurnizorTab(tab, liveTabs);
        if (!liveTab) {
            continue;
        }
        if (isFurnizorTabPriceModified(tab, liveTab)) {
            return true;
        }
        const draftSale = readTabSalePrice(tab);
        const liveSale = readTabSalePrice(liveTab);
        if (
            draftSale != null
            && liveSale != null
            && pricesMeaningfullyDiffer(liveSale, draftSale)
        ) {
            return true;
        }
        const draftEntry = readTabEntryPrice(tab);
        const liveEntry = readTabEntryPrice(liveTab);
        if (
            draftEntry != null
            && liveEntry != null
            && pricesMeaningfullyDiffer(liveEntry, draftEntry)
        ) {
            return true;
        }
    }
    return false;
}

/**
 * Tip A: entry price and/or sale price changed (any cost/transport/markup cause).
 * Includes the same change on a non-default furnizor.
 * New products (action=create) always count as tip A.
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @returns {boolean}
 */
export function draftHasEntryOrSalePriceChange(draft, liveItem = null) {
    if (String(draft?.action || '').trim() === 'create') {
        return true;
    }

    const prevSale = toNullableNumber(
        liveItem?.price
            ?? draft?.previousPrice
            ?? draft?.summary?.previous_price,
    );
    const nextSale = toNullableNumber(
        draft?.summary?.price ?? draft?.payload?.price,
    );
    if (pricesMeaningfullyDiffer(prevSale, nextSale)) {
        return true;
    }

    if (nonDefaultFurnizorHasEntryOrSalePriceChange(draft, liveItem)) {
        return true;
    }

    const tabs = selectDraftFurnizorTabsForDisplay(draft, liveItem);
    const tabsToCheck = tabs.length ? tabs : [null];
    for (const tab of tabsToCheck) {
        const pricing = resolveDraftTablePricing(draft, liveItem, tab);
        if (pricesMeaningfullyDiffer(pricing.previous_price, pricing.price)) {
            return true;
        }
        if (pricesMeaningfullyDiffer(
            pricing.entry_price_ron_inc_vat_old,
            pricing.entry_price_ron_inc_vat,
        )) {
            return true;
        }
    }
    return false;
}

/**
 * Tip B: real non-price product/meta/furnizor-structure change vs live.
 * Sparse / missing live fields do NOT count (avoids false „alte modificări”
 * that would spray publish onto the last 5 lists).
 *
 * @param {Object} draft
 * @param {Object|null} [liveItem]
 * @returns {boolean}
 */
export function draftHasNonPriceChange(draft, liveItem = null) {
    if (String(draft?.action || '').trim() === 'create') {
        // Creates are tip A (new product / new list membership), not tip B.
        return false;
    }
    if (!liveItem) {
        // Without live compare we cannot prove a non-price change — never tip B.
        return false;
    }

    const payload = draft?.payload || {};
    const detail = payload.pricing_detail || {};
    const liveDetail = liveItem.pricing_detail || {};

    if (draftFieldMeaningfullyDiffers(
        payload.product_name ?? draft?.summary?.product_name,
        liveItem.product_name ?? liveItem.name,
    )) {
        return true;
    }
    if (draftFieldMeaningfullyDiffers(
        payload.measurement_unit,
        liveItem.measurement_unit,
    )) {
        return true;
    }
    if (draftFieldMeaningfullyDiffers(payload.status, liveItem.status)) {
        return true;
    }
    if (draftFieldMeaningfullyDiffers(
        payload.visibleInOfferApp ?? payload.visible_in_offer_app,
        liveItem.visible_in_offer_app ?? liveItem.visibleInOfferApp,
        'bool',
    )) {
        return true;
    }

    const draftCode = normText(
        payload.product_code || draft?.sourceProductCode || draft?.product_code,
    );
    const liveCode = normText(liveItem.product_code || liveItem.part_no);
    const oldCode = normText(payload.old_product_code || draft?.sourceProductCode);
    if (draftCode && liveCode && draftCode !== liveCode) {
        return true;
    }
    if (oldCode && draftCode && oldCode !== draftCode) {
        return true;
    }

    if (Array.isArray(payload.category_assignments)
        && draftFieldMeaningfullyDiffers(
            payload.category_assignments,
            liveItem.category_assignments,
            'json',
        )) {
        return true;
    }
    if (Array.isArray(payload.categories)
        && draftFieldMeaningfullyDiffers(
            payload.categories,
            liveItem.categories,
            'json',
        )) {
        return true;
    }

    const detailTextKeys = [
        ['manufacturerName', 'manufacturer_name'],
        ['manufacturerCode', 'manufacturer_code'],
        ['manufacturerProductCode', 'manufacturer_product_code'],
        ['manufacturerProductName', 'manufacturer_product_name'],
        ['serialNumber', 'serial_number'],
        ['nomenclatureCode', 'nomenclature_code'],
        ['productNotes', 'product_notes'],
        ['notes', 'notes'],
        ['weight', 'weight'],
        ['chapterName', 'chapter_name'],
        ['subchapterName', 'subchapter_name'],
    ];
    for (const [camel, snake] of detailTextKeys) {
        const draftVal = detail[camel] ?? detail[snake];
        if (draftVal === undefined) {
            continue;
        }
        const liveVal = liveDetail[camel]
            ?? liveDetail[snake]
            ?? liveItem[snake]
            ?? liveItem[camel];
        if (draftFieldMeaningfullyDiffers(draftVal, liveVal)) {
            return true;
        }
    }

    if (Object.prototype.hasOwnProperty.call(detail, 'isCompositeProduct')
        || Object.prototype.hasOwnProperty.call(detail, 'is_composite_product')) {
        if (draftFieldMeaningfullyDiffers(
            detail.isCompositeProduct ?? detail.is_composite_product,
            liveDetail.isCompositeProduct
                ?? liveDetail.is_composite_product
                ?? liveItem.is_composite_product,
            'bool',
        )) {
            return true;
        }
    }
    if (Array.isArray(detail.systems)
        && draftFieldMeaningfullyDiffers(
            detail.systems,
            liveDetail.systems ?? liveItem.systems,
            'json',
        )) {
        return true;
    }
    if (Array.isArray(detail.systemSubcategories)
        && draftFieldMeaningfullyDiffers(
            detail.systemSubcategories,
            liveDetail.systemSubcategories ?? liveItem.system_subcategories,
            'json',
        )) {
        return true;
    }
    if (Array.isArray(detail.componentProductCodes)
        && draftFieldMeaningfullyDiffers(
            detail.componentProductCodes,
            liveDetail.componentProductCodes
                ?? liveDetail.component_product_codes,
            'json',
        )) {
        return true;
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const draftTabs = Array.isArray(detail.furnizorPricings)
        ? detail.furnizorPricings
        : (Array.isArray(detail.furnizor_pricings) ? detail.furnizor_pricings : []);
    if (draftTabs.length) {
        for (const tab of draftTabs) {
            const liveTab = findMatchingLiveFurnizorTab(tab, liveTabs);
            if (!liveTab) {
                // New furnizor tab on an existing product = tip B structure change
                // (even when sale/entry prices happen to match).
                return true;
            }
            // Price inputs (supplier list, discounts, transport, markup, sale)
            // are tip A, including on a non-default furnizor. Identity only
            // (who the supplier is) stays tip B.
            if (isFurnizorTabStructureModified(tab, liveTab)) {
                return true;
            }
        }
        // Removed a live furnizor that the draft no longer carries.
        if (liveTabs.length > draftTabs.length) {
            const draftStillHas = (liveTab) => (
                findMatchingLiveFurnizorTab(liveTab, draftTabs) != null
            );
            if (liveTabs.some((liveTab) => !draftStillHas(liveTab))) {
                return true;
            }
        }
    }

    return false;
}

/**
 * @param {Array<Object>} drafts
 * @param {Map<string, Object>|Object|null} [liveItemsByCode]
 * @returns {{
 *   priceDrafts: Array<Object>,
 *   otherDrafts: Array<Object>,
 *   priceCount: number,
 *   otherCount: number,
 * }}
 */
export function classifyPublishDrafts(drafts = [], liveItemsByCode = null) {
    const lookup = (code) => {
        if (!code || !liveItemsByCode) {
            return null;
        }
        if (liveItemsByCode instanceof Map) {
            return liveItemsByCode.get(code) || null;
        }
        return liveItemsByCode[code] || null;
    };

    const priceDrafts = [];
    const otherDrafts = [];
    for (const draft of drafts || []) {
        const code = String(
            draft?.payload?.product_code
                || draft?.sourceProductCode
                || draft?.product_code
                || '',
        ).trim();
        const live = lookup(code);
        if (draftHasEntryOrSalePriceChange(draft, live)) {
            // Tip A wins: price changes go only to current/new list, never last 5.
            priceDrafts.push(draft);
        } else if (draftHasNonPriceChange(draft, live)) {
            otherDrafts.push(draft);
        } else {
            // No proven tip-B change — do not activate last-5 publish.
            // Keep with tip-A targets (current / listă nouă) if user applies them.
            priceDrafts.push(draft);
        }
    }
    return {
        priceDrafts,
        otherDrafts,
        priceCount: priceDrafts.length,
        otherCount: otherDrafts.length,
    };
}

/**
 * Latest N price lists for a type (date_applied desc, then id desc).
 * Always includes currentPriceListId when provided.
 *
 * @param {Array<Object>} priceLists
 * @param {Object} [options]
 * @param {number} [options.limit=5]
 * @param {number|string|null} [options.currentPriceListId]
 * @param {string|null} [options.priceListType]
 * @returns {Array<number|string>}
 */
export function resolveLatestPriceListIds(priceLists = [], {
    limit = 5,
    currentPriceListId = null,
    priceListType = null,
} = {}) {
    const max = Math.max(1, Number(limit) || 5);
    let lists = Array.isArray(priceLists) ? [...priceLists] : [];
    if (priceListType) {
        lists = lists.filter((list) => (
            !list?.type || String(list.type) === String(priceListType)
        ));
    }
    lists.sort((a, b) => {
        const dateA = String(a?.date_applied || '');
        const dateB = String(b?.date_applied || '');
        if (dateA !== dateB) {
            return dateB.localeCompare(dateA);
        }
        return Number(b?.id || 0) - Number(a?.id || 0);
    });

    const ids = [];
    const seen = new Set();
    const pushId = (rawId) => {
        if (rawId == null || rawId === '') {
            return;
        }
        const key = String(rawId);
        if (seen.has(key)) {
            return;
        }
        seen.add(key);
        ids.push(rawId);
    };

    if (currentPriceListId != null && currentPriceListId !== '') {
        pushId(currentPriceListId);
    }
    for (const list of lists) {
        if (ids.length >= max) {
            break;
        }
        pushId(list?.id);
    }
    return ids;
}

/**
 * Build publish batches for automatic apply.
 *
 * Tip A = entry/sale price change → current or new list only.
 * Tip B = proven non-price change → last 5 (never activated when otherDrafts is empty).
 *
 * mode `current` (maxim în lista curentă):
 *   A → [current], B → last 5
 * mode `new`:
 *   A → [newList], B → last 5 + newList
 * mode `other_only` (doar tip B):
 *   B → last 5
 *
 * @param {Object} params
 * @param {Array<Object>} params.priceDrafts
 * @param {Array<Object>} params.otherDrafts
 * @param {'current'|'new'|'other_only'} params.mode
 * @param {number|string|null} params.currentPriceListId
 * @param {Array<number|string>} params.lastFiveIds
 * @param {number|string|null} [params.newPriceListId]
 * @returns {Array<{ draftIds: Array<number|string>, priceListIds: Array<number|string>, label: string }>}
 */
export function buildAutoPublishBatches({
    priceDrafts = [],
    otherDrafts = [],
    mode,
    currentPriceListId = null,
    lastFiveIds = [],
    newPriceListId = null,
} = {}) {
    const priceIds = priceDrafts.map((d) => d.id).filter((id) => id != null && id !== '');
    const otherIds = otherDrafts.map((d) => d.id).filter((id) => id != null && id !== '');
    const lastFive = Array.isArray(lastFiveIds) ? [...lastFiveIds] : [];
    const batches = [];

    const uniqueIds = (ids) => {
        const out = [];
        const seen = new Set();
        for (const id of ids) {
            if (id == null || id === '') {
                continue;
            }
            const key = String(id);
            if (seen.has(key)) {
                continue;
            }
            seen.add(key);
            out.push(id);
        }
        return out;
    };

    if (mode === 'other_only') {
        if (otherIds.length && lastFive.length) {
            batches.push({
                draftIds: otherIds,
                priceListIds: uniqueIds(lastFive),
                label: 'alte modificări → ultimele liste',
            });
        }
        return batches;
    }

    if (mode === 'current') {
        if (priceIds.length && currentPriceListId != null && currentPriceListId !== '') {
            batches.push({
                draftIds: priceIds,
                priceListIds: [currentPriceListId],
                label: 'prețuri → lista curentă',
            });
        }
        if (otherIds.length && lastFive.length) {
            batches.push({
                draftIds: otherIds,
                priceListIds: uniqueIds(lastFive),
                label: 'alte modificări → ultimele liste',
            });
        }
        return batches;
    }

    // mode === 'new'
    if (priceIds.length && newPriceListId != null && newPriceListId !== '') {
        batches.push({
            draftIds: priceIds,
            priceListIds: [newPriceListId],
            label: 'prețuri → listă nouă',
        });
    }
    if (otherIds.length) {
        const targets = uniqueIds([
            ...lastFive,
            newPriceListId,
        ]);
        if (targets.length) {
            batches.push({
                draftIds: otherIds,
                priceListIds: targets,
                label: 'alte modificări → ultimele liste + listă nouă',
            });
        }
    }
    return batches;
}
